import { Flag } from 'src/app/flag/model/flag';
import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { Code } from 'src/app/core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { sortBy, cloneDeep } from 'lodash';
import { IntlService } from '@progress/kendo-angular-intl';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SettingService } from 'src/app/core/service/setting.service';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';

@Component({
  standalone: false,
  selector: 'app-flag-edit',
  templateUrl: './flag-edit.component.html',
  styleUrls: ['./flag-edit.component.scss']
})
export class FlagEditComponent
{
  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;
  @ViewChild('multiSelect', { static: false }) public multiSelect: MultiSelectComponent;
  @ViewChild('singleSelect', { static: false }) public singleSelect: MultiSelectComponent;

  @Input() public memberId: any;
  @Input() public caseId: any;

  @Output() public save: EventEmitter<Flag[]> = new EventEmitter();
  @Output() public cancelling: EventEmitter<Flag> = new EventEmitter();
  @Output() public delete: EventEmitter<Flag> = new EventEmitter();

  public loaded = false;
  public flagOpened = false;
  public isNew = false;
  public deleteConfirmationOpened = false;
  public flagForm: any;
  public flagCategories: Code[];
  public flagPriorities: Code[];
  public flags: Code[];
  public allFlags: Code[];
  public flag: Flag;
  public patientLabel = {};
  public currentDictionaryUsed = null;
  public previousDictionaryUsed = null;

  /**
   * Filtered list of flags for drop-down.
   */
  public flagsFiltered: Code[] = [];
  public allFlagsFiltered: Code[] = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Maximum number of items for flag virtualization. This is to accelerate the DOM performance.
   */
  public virtualFlag: any = {
    itemHeight: 30
  };

  constructor(
    private fb: UntypedFormBuilder,
    private dictionaryService: DictionaryService,
    public intl: IntlService,
    private settingService: SettingService)
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });

    this.dictionaryService.getCachedFlagPriorities().subscribe({
      next: (data) =>
      {
        this.flagPriorities = sortBy(data, ['id']);
      }
    });

    this.dictionaryService.getFlagCategories().subscribe({
      next: (data) =>
      {
        this.flagCategories = sortBy(data, ['displayName']);
      }
    });

    this.dictionaryService.getCachedFlags().subscribe({
      next: (data) =>
      {
        data.forEach(d =>
        {
          if (d.displayName.includes('(') && d.displayName.includes(')'))
          {
            return;
          }
          d.displayName = `${d.displayName} (${d.code})`;
        });
        this.flags = sortBy(data, ['displayName']);
        this.allFlags = this.flags;
        this.filterFlags('');
      }
    });
  }

  public get description(): UntypedFormControl
  {
    return this.flagForm.get('description');
  }

  public get category(): UntypedFormControl
  {
    return this.flagForm.get('category');
  }

  public get priority(): UntypedFormControl
  {
    return this.flagForm.get('priority');
  }

  @Input() public set model(flag: Flag)
  {
    this.initModel(flag);
  }

  /**
   * Normalizer for the multiple flags description. This is required to handle the free text within a single control.
   * The multiple flags description can contains valid users or direct addresses.
   */
  public flagNormalizer = (text$: Observable<string>): any => text$.pipe(map((text: string) =>
  {
    // Search for matching item to avoid duplicates
    if (this.description.value != null)
    {
      const matchingValue: any = this.description.value.find((item: any) => item.displayName.toLowerCase() === text.toLowerCase());
      if (matchingValue)
      {
        return matchingValue; // Return the already selected matching value and the component will remove it
      }
    }

    // Search in data
    const matchingItem: any = this.allFlagsFiltered.find((item: any) => item.displayName.toLowerCase() === text.toLowerCase());

    if (matchingItem)
    {
      return matchingItem;
    }
    else
    {
      return { id: null, displayName: text };
    }
  }));

  public onSave(e: Event): void
  {
    e.preventDefault();

    const flag = this.flagForm.value;
    if (this.memberId !== undefined)
    {
      flag.memberId = this.memberId ? this.memberId : null;
    }
    if (this.caseId !== undefined)
    {
      flag.caseId = this.caseId ? this.caseId : null;
    }
    if (flag.privacy === true)
    {
      flag.privacy = 'privated';
    }
    else
    {
      flag.privacy = 'shared';
    }
    flag.version = (flag.version !== null) ? flag.version : 0;

    const flags: Flag[] = [];

    // Set Flag Type reference if selected from list
    if (!this.isNew)
    {
      const flagType = this.allFlags.find(x => x.displayName === flag.description);
      flag.flagType = (flagType !== undefined) ? flagType : null;
      flags.push(flag);
    }
    else
    {
      this.description.value.forEach((x: any) =>
      {
        const newFlag = cloneDeep(flag);
        const flagType = this.allFlags.find(f => f.displayName === x.displayName);
        newFlag.flagType = (flagType !== undefined) ? flagType : null;
        newFlag.description = x.displayName;
        flags.push(newFlag);
      });
    }
    this.save.emit(flags);
    this.flagOpened = false;
  }

  /**
   * Filters the flags based on user input.
   */
  public filterFlags(filter: string): void
  {
    this.searchTerm = filter;
    this.flagsFiltered = this.flags
      .filter(flag => flag.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Filters all flags based on user input.
   */
  public filterAllFlags(filter: string): void
  {
    this.searchTerm = filter;
    if (this.flagForm.get('category').value != null)
    {
      if (this.flagForm.get('category').value.codeSetPerRecord != null)
      {
        this.dictionaryService.getCodesByCodeSetId(this.flagForm.get('category').value.codeSetPerRecord.id).subscribe({
          next: (data) =>
          {
            this.allFlags = sortBy(data, ['displayName']);
            this.allFlagsFiltered = this.allFlags
              .filter(flag => flag.displayName.toLowerCase().includes(filter.toLowerCase()));
          }
        });
      }
      else
      {
        this.allFlags = this.flags;
        this.allFlagsFiltered = this.allFlags
          .filter(flag => flag.displayName.toLowerCase().includes(filter.toLowerCase()));
      }
    }
  }

  public onDelete(e: Event): void
  {
    e.preventDefault();
    const flag = this.flagForm.value;
    this.delete.emit(flag);
    this.flagOpened = false;
  }

  public getDialogWidth(): any
  {
    return '800px';
  }

  public showTooltip(e: ElementRef): void
  {
    this.tooltipDir.toggle(e, true);
  }
  
  public hideTooltip(): void
  {
    this.tooltipDir.hide();
  }

  private getFlags(event: any, initializationContext: boolean)
  {
    if (event && event !== undefined && event.id !== undefined)
    {
      if (event.codeSetPerRecord != null)
      {
        this.currentDictionaryUsed = 1;
        this.dictionaryService.getCodesByCodeSetId(event.codeSetPerRecord.id).subscribe({
          next: (data) =>
          {
            this.allFlagsFiltered = sortBy(data, ['displayName']);
          }
        });
      }
      else
      {
        this.currentDictionaryUsed = 2;
        this.allFlagsFiltered = this.flagsFiltered;
      }
      // Reset only if the dictionary has changed
      if (!initializationContext)
      {
        if ((this.currentDictionaryUsed !== this.previousDictionaryUsed) || (this.currentDictionaryUsed === 1))
        {
          this.clearFlags();
        }
      }
      this.previousDictionaryUsed = this.currentDictionaryUsed;
    }
  }

  private initModel(flag: Flag)
  {
    if (flag === undefined)
    {
      return;
    }
    // Set form
    this.flagForm = this.fb.group({
      id: [flag.id],
      description: [flag.description, Validators.required],
      priority: [flag.priority, Validators.required],
      category: [flag.category, Validators.required],
      privacy: [(flag.privacy === 'shared') ? false : true, Validators.required],
      version: [flag.version]
    });

    this.isNew = flag.id === undefined;
    this.flag = flag;
    this.loaded = true;

    this.flagOpened = flag !== undefined;

    this.flagForm.get('category').valueChanges.subscribe({
      next: (category: string) =>
      {
        this.setControlState(category);
      }
    });
    this.setControlState(this.category.value);
    this.getFlags(this.category.value, true);
  }

  private setControlState(category: string): void
  {
    if (category !== null && category !== undefined && category !== '')
    {
      this.description.enable();
    }
    else
    {
      this.description.disable();
      this.clearFlags();
    }
  }

  private clearFlags(): void
  {
    this.flagForm.get('description').value = null;
    if (this.multiSelect !== undefined)
    {
      this.multiSelect.reset();
    }
    else
      if (this.singleSelect !== undefined)
      {
        this.singleSelect.reset();
      }
  }

  private closeEditForm(): void
  {
    this.flagOpened = false;
    this.cancelling.emit();
  }
}
